export const CHECKOUT_URL = "https://occlusionbp-en.impultienda.ar/checkout/";

export const images = {
  main: "https://cdn.magicpatterns.com/uploads/wC5GWk7QhEuYwo2hpcbiFc/MOCKUP_PRINCIPAL_SIN_FONDO.png",
  author: "https://cdn.magicpatterns.com/uploads/5chtti79QXt5jEwdpkQTry/Captura_de_pantalla_2026-10-14_162339.png",
};

export const whatYouGet = [
  { title: "6 Specialized Volumes", text: "Foundations of occlusion, clinical assessment, functional analysis, restorative planning, complication review, and case documentation." },
  { title: "30+ Practical Clinical Resources", text: "Checklists, assessment templates, visual reference sheets, documentation tools, and educational case exercises." },
  { title: "5 Exclusive Bonuses", text: "Additional resources designed to help you organize your clinical workflow and reinforce your understanding." },
  { title: "100% Digital Access", text: "Download the materials and consult them on your computer, tablet, or phone." },
];

export const before = [
  "You review multiple references before organizing a complex occlusal case.",
  "You struggle to keep your assessment notes consistent from one patient to another.",
  "You find it difficult to bring clinical findings together in one structured overview.",
  "You spend time searching for the right checklist, diagram, or reference.",
  "You worry about overlooking information that should be documented or investigated further.",
  "Every complex case seems to require rebuilding your workflow from scratch.",
];

export const after = [
  "You follow a structured framework for organizing your occlusal assessment.",
  "You use consistent templates to document relevant clinical findings.",
  "You organize static and dynamic observations within a clear framework.",
  "You can locate the appropriate reference resource more easily.",
  "You have checklists to help identify missing information and questions requiring further evaluation.",
  "You use the same organizational method across different cases, adapting it to each patient's needs.",
];

export const painPoints = [
  { icon: "case", title: "You Have a Complex Restorative Case in Front of You", text: "You're reviewing a patient with extensive tooth wear or a complicated restorative history, and you want a structured way to organize the occlusal information before proceeding." },
  { icon: "time", title: "You Spend Too Much Time Searching for Information", text: "You find yourself moving between textbooks, articles, personal notes, and reference materials just to prepare a consistent assessment." },
  { icon: "think", title: "You Want to Organize Your Occlusal Findings More Clearly", text: "You understand the relevant concepts, but you would like a more systematic way to document what you observe and identify what still needs investigation." },
  { icon: "list", title: "You Want a Consistent Assessment Process", text: "You need a repeatable framework that helps you review the relevant information without relying entirely on memory." },
  { icon: "link", title: "You Want to Integrate Function and Restorative Planning", text: "You want a clearer way to organize the relationship between your occlusal observations, restorative objectives, and the additional information required to plan a case." },
  { icon: "book", title: "You Want Practical Resources, Not Another Textbook", text: "You want visual tools, templates, checklists, and structured reference material that you can consult when needed." },
] as const;

export const volumes = [
  {
    number: "I",
    title: "Build a Structured Foundation for Occlusal Assessment",
    intro: "Resources designed to help you organize the foundations of occlusion, review relevant terminology, and prepare for a structured assessment.",
    items: ["A visual overview of the main concepts of dental occlusion", "An illustrated terminology reference", "A structured occlusal assessment checklist", "A worksheet for organizing patient history and presenting concerns", "A guide to documenting relevant clinical findings", "A visual index of the concepts covered throughout the system"],
    note: "The goal is to create a consistent starting point for reviewing the information relevant to each case.",
  },
  {
    number: "II",
    title: "Organize Static Occlusal Findings",
    intro: "This volume focuses on organizing static occlusal findings and identifying the information that needs to be recorded.",
    items: ["A structured worksheet for recording observed contacts", "A visual guide to organizing occlusal findings", "Templates for documenting restorative context", "A checklist for identifying missing assessment information", "A matrix for summarizing observations", "Educational examples of structured clinical documentation"],
    note: "Designed to support your assessment process, not to determine a diagnosis or treatment automatically.",
  },
  {
    number: "III",
    title: "Structure Your Review of Mandibular Movements and Functional Findings",
    intro: "Bring mandibular movements and functional observations together in a way that is easy to review and document.",
    items: ["A visual reference for organizing mandibular movement observations", "A worksheet for recording relevant findings", "A template for documenting observed contacts during movement", "A matrix for organizing functional observations", "A checklist of information that may require additional assessment", "Educational case exercises for reviewing documented findings"],
    note: "A consistent structure for recording and discussing clinical observations.",
  },
  {
    number: "IV",
    title: "Organize Restorative Planning and Occlusal Considerations",
    intro: "Organize the interacting factors of complex restorative cases before documenting the objectives and alternatives being considered.",
    items: ["A restorative case planning worksheet", "A matrix for organizing clinical findings and restorative objectives", "A template for documenting vertical dimension of occlusion considerations", "A checklist for recording relevant information before restorative treatment", "A worksheet for organizing interdisciplinary considerations", "A case presentation template"],
    note: "These tools do not provide universal criteria for changing vertical dimension or selecting a restorative treatment. Such decisions require individualized clinical assessment.",
  },
  {
    number: "V",
    title: "Review Potential Complications and Follow-Up Considerations",
    intro: "Resources for organizing potential complications and planning how the case will be reviewed over time.",
    items: ["A complication documentation worksheet", "A template for recording changes in symptoms", "A checklist for organizing follow-up observations", "A worksheet for documenting adjustments and their rationale", "A template for recording unresolved questions", "Educational cases involving restorative and occlusal considerations"],
    note: "Maintain organized records and identify issues requiring appropriate clinical evaluation.",
  },
  {
    number: "VI",
    title: "Turn Your Clinical Findings Into a Clear Case Presentation",
    intro: "Communicate your findings, objectives, and proposed next steps clearly and professionally.",
    items: ["A complete case presentation template", "A clinical findings summary sheet", "A treatment objectives worksheet", "A template for documenting alternatives considered", "A patient communication planning sheet", "A follow-up documentation template", "A guide to organizing the information gathered throughout the assessment"],
    note: "Present your reasoning clearly while leaving clinical decisions where they belong: with the treating professional.",
  },
];

export const toolkit = [
  { icon: "template", title: "Structured Assessment Templates", text: "Organize patient history, clinical observations, and relevant assessment information in a consistent format." },
  { icon: "check", title: "Professional Checklists", text: "Review the information gathered and identify questions that may require additional investigation." },
  { icon: "visual", title: "Visual Reference Sheets", text: "Access diagrams and reference pages that make key concepts easier to review." },
  { icon: "matrix", title: "Case Organization Matrices", text: "Bring findings, restorative objectives, and outstanding questions together in one overview." },
  { icon: "camera", title: "Documentation Worksheets", text: "Organize the information associated with a case, including records and follow-up observations." },
  { icon: "book", title: "Educational Case Exercises", text: "Practice structuring your analysis using fictional clinical scenarios designed for education." },
] as const;

export const bonuses = [
  {
    number: 1,
    title: "The Occlusion Casebook",
    value: 47,
    image: "https://cdn.magicpatterns.com/uploads/f2bEpBqtqE6ofNgbQ2F124/MOCKUP_1_DE_OCLUSSION_EN.png",
    text: "Learn how to organize your analysis of different fictional occlusal and restorative scenarios — practice reviewing information, identifying unanswered questions, and structuring your clinical reasoning.",
    items: ["Fictional educational case scenarios", "Structured case review worksheets", "Questions for organizing clinical observations", "Discussion prompts", "Suggested documentation formats"],
  },
  {
    number: 2,
    title: "The Occlusal Assessment Checklist Pack",
    value: 37,
    image: "https://cdn.magicpatterns.com/uploads/2zcHYups3uiankSBfhNdUz/MOCKUP_2_DE_OCLUSSION_EN.png",
    text: "A practical collection of checklists designed to help you organize the information gathered during an occlusal assessment.",
    items: ["Initial assessment checklist", "Patient history worksheet", "Static findings documentation checklist", "Functional observations checklist", "Restorative planning checklist", "Follow-up documentation checklist"],
  },
  {
    number: 3,
    title: "The Occlusion Visual Atlas",
    value: 47,
    image: "https://cdn.magicpatterns.com/uploads/nQ5cr5RttthvL9ddP7wnAC/MOCKUP_3_DE_OCLUSSION_EN.png",
    text: "A visual reference collection designed to make the organization and review of occlusal concepts easier.",
    items: ["Illustrated terminology", "Visual reference diagrams", "Occlusal assessment maps", "Mandibular movement reference sheets", "Restorative planning diagrams", "A visual index for quick navigation"],
  },
  {
    number: 4,
    title: "The Restorative Case Presentation Kit",
    value: 37,
    image: "https://cdn.magicpatterns.com/uploads/kFAPBv2gvFSLcWdGABp3EY/MOCKUP_4_DE_OCLUSSION_EN.png",
    text: "Organize the information in your cases and communicate your findings more clearly — without relying on improvised notes.",
    items: ["Case presentation templates", "Clinical findings summary sheets", "Treatment objectives worksheets", "Documentation planning templates", "Interdisciplinary communication worksheets", "Patient discussion planning sheets"],
  },
  {
    number: 5,
    title: "The Clinical Documentation Master Pack",
    value: 29,
    image: "https://cdn.magicpatterns.com/uploads/6DXMXDWvL5ZmKepuvuhvs8/MOCKUP_5_DE_OCLUSSION_EN.png",
    text: "Reusable worksheets designed to support consistent documentation throughout the assessment and restorative planning process.",
    items: ["Patient assessment forms", "Clinical observations worksheets", "Occlusal findings matrices", "Restorative planning templates", "Follow-up documentation forms", "Case review checklists"],
  },
];

export const offerItems = [
  { label: "OCCLUSION BLUEPRINT™ — Complete Six-Volume Collection", value: 97 },
  { label: "Bonus #1 — The Occlusion Casebook", value: 47 },
  { label: "Bonus #2 — The Occlusal Assessment Checklist Pack", value: 37 },
  { label: "Bonus #3 — The Occlusion Visual Atlas", value: 47 },
  { label: "Bonus #4 — The Restorative Case Presentation Kit", value: 37 },
  { label: "Bonus #5 — The Clinical Documentation Master Pack", value: 29 },
];

export const guaranteeItems = [
  "Explore all six volumes",
  "Review the visual references",
  "Examine the assessment templates",
  "Use the documentation worksheets",
  "Explore the educational case exercises",
  "Decide whether the system meets your expectations",
];

export const testimonials = [
  {
    name: "Dr. Sarah Mitchell",
    role: "General Dentist · Denver, CO",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/14f3fc5d-f2d0-46ff-a5b4-2e9666fa3243.jpg",
    quote: "The assessment checklist became the first thing I open for every worn-dentition case. My notes finally look the same from one patient to the next.",
  },
  {
    name: "Dr. Marcus Johnson",
    role: "Restorative Dentist · Atlanta, GA",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/0eddf031-6588-45e5-8df5-8cbe1ae09742.jpg",
    quote: "The case organization matrices are the most useful part for me. Findings, objectives and open questions sit on one page instead of three notebooks.",
  },
  {
    name: "Dr. Emily Chen",
    role: "Associate Dentist · Vancouver, BC",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/f5e5675f-ee33-4f20-9ffc-2b95816e564c.jpg",
    quote: "Clear, visual and easy to navigate. The Visual Atlas helped me review mandibular movement concepts before a complex case discussion.",
  },
  {
    name: "Dr. Richard Hayes",
    role: "Prosthodontics Practice · Leeds, UK",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/bc13d8c4-4289-40c6-bdee-9ae580c4e27d.jpg",
    quote: "The presentation kit saved me time preparing interdisciplinary meetings. I'd love even more editable templates in a future update.",
  },
];

export const faqs = [
  { q: "How Will I Receive the Materials?", a: "After completing your purchase, you'll receive instructions for accessing and downloading OCCLUSION BLUEPRINT and all five bonuses in digital format." },
  { q: "Is OCCLUSION BLUEPRINT Suitable for Dentists in Different Countries?", a: "The educational framework and documentation resources are designed for professional use across different settings. However, clinical standards, professional regulations, and treatment recommendations may vary by country. Always adapt your practice to applicable local guidance and professional requirements." },
  { q: "Do I Need to Be an Occlusion Specialist?", a: "The materials are intended for dental professionals who want to organize and expand their understanding of occlusion. The collection is an educational reference, not a substitute for specialist training, supervised clinical experience, or individualized professional judgment." },
  { q: "Can I Download the Files?", a: "Yes. The materials are delivered digitally so you can download them and consult them on compatible devices." },
  { q: "Can I Access the Materials on My Phone or Tablet?", a: "Yes. You can access the downloaded files on compatible computers, tablets, and phones. Some printable templates may be easier to complete on a larger screen or on paper." },
  { q: "Is This a One-Time Payment?", a: "Yes. The listed purchase price is a one-time payment of $27 USD, with no recurring subscription fee." },
  { q: "Does OCCLUSION BLUEPRINT Tell Me Exactly How to Treat Every Patient?", a: "No. The system is designed to organize information, support learning, and structure documentation. It does not provide automatic diagnoses, guarantee treatment outcomes, or replace an individualized clinical assessment." },
  { q: "What If the Product Doesn't Meet My Expectations?", a: "Your purchase includes a 7-day satisfaction guarantee. Follow the refund instructions provided with your purchase if you decide the product isn't right for you." },
];

export const buyers = [
  { name: "Dr. Alejandro Martínez", country: "Mexico" },
  { name: "Dra. Valentina Rojas", country: "Chile" },
  { name: "Dr. Santiago Gómez", country: "Colombia" },
  { name: "Dra. Lucía Fernández", country: "Spain" },
  { name: "Dr. Mateo Pereyra", country: "Argentina" },
  { name: "Dra. Camila Torres", country: "Peru" },
  { name: "Dr. Diego Herrera", country: "Ecuador" },
  { name: "Dra. Sofía Navarro", country: "Uruguay" },
  { name: "Dr. Andrés Castillo", country: "Costa Rica" },
  { name: "Dra. Isabela Morales", country: "Dominican Republic" },
  { name: "Dr. Javier Ruiz", country: "Guatemala" },
  { name: "Dra. Mariana López", country: "Panama" },
  { name: "Dr. Gabriel Duarte", country: "Paraguay" },
  { name: "Dra. Daniela Vargas", country: "Bolivia" },
];
